import React, { useCallback, useEffect, useState } from 'react';
import { Skeleton, Typography } from '@mui/material';
import { showAxiosMessageError } from '../../utils/error-message.util';
import { useSnackbars } from '../../hooks/snackbar.hook';
import { getCurrentAccountUser } from '../../services/user.service';
import { UserAccountDto } from '../../services/dtos/user-account.dto';
import AccountUi from '../../components/userInterfaces/account/account.ui';
import MyPreferencesForm from '../../components/forms/account/my-preferences.form';
import { NotificationPreferenceDto } from '../../services/dtos/notification-preference.dto';
import {
    addNotificationPreference,
    deleteNotificationPreference,
} from '../../services/notification-preference.service';
import './account.page.scss';

const updateNotificationPreferences = (
    currentUser: UserAccountDto | undefined,
    pref: NotificationPreferenceDto,
    updater: (prefMap: NotificationPreferenceDto) => NotificationPreferenceDto
) => {
    if (!currentUser) {
        return currentUser;
    }

    return {
        ...currentUser,
        notificationPreferences: currentUser.notificationPreferences?.map(prefMap => {
            if (prefMap.userId === pref.userId && prefMap.mandateId === pref.mandateId) {
                return updater(prefMap);
            }

            return prefMap;
        }),
    };
};

/**
 * Page de compte de l'utilisateur
 * @constructor
 */
export default function AccountPage() {
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    // Données de l'utilisateur nécessaires pour la page en cours
    const [accountUser, setAccountUser] = useState<UserAccountDto>();
    // Gestion du dialog pour le signalement de changements
    const [open, setOpen] = React.useState(false);
    // bloc les checkBox
    const [disabled, setDisabled] = React.useState(false);
    //Handler qui gère l'ouverture de la popup "signaler un changement"
    const handleClickOpen = () => {
        setOpen(true);
    };

    //Handler qui gère la fermeture de la popup
    const handleClose = () => {
        setOpen(false);
    };

    const refreshAccountUser = useCallback(() => {
        getCurrentAccountUser()
            .then(result => {
                setAccountUser(result);
            })
            .catch(error => {
                console.error("Erreur lors de la récupération de l'utilisateur");
                showAxiosMessageError(error, showMessage);
            });
    }, [showMessage]);

    useEffect(() => {
        //Récupération de l'utilisateur courant
        refreshAccountUser();
    }, [refreshAccountUser]);

    const handlerChangePrefs = (pref: NotificationPreferenceDto, checked: boolean) => {
        setDisabled(true);
        if (checked) {
            const restorePreference = pref.id
                ? deleteNotificationPreference(pref.id).then(() => pref.id)
                : Promise.resolve(pref.id);

            restorePreference
                .then(() => {
                    setAccountUser(currentUser => {
                        return updateNotificationPreferences(currentUser, pref, prefMap => ({
                            ...prefMap,
                            checked: true,
                            id: 0,
                        }));
                    });
                    showMessage.successAlert('Mise à jour réussie');
                })
                .catch(error => {
                    console.error("Erreur lors de la réactivation d'une préférence");
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setDisabled(false);
                });
        } else {
            addNotificationPreference(pref.userId, pref.mandateId)
                .then(result => {
                    setAccountUser(currentUser => {
                        return updateNotificationPreferences(currentUser, pref, prefMap => ({
                            ...prefMap,
                            checked: false,
                            id: result,
                        }));
                    });
                    showMessage.successAlert('Mise à jour réussie');
                })
                .catch(error => {
                    console.error("Erreur lors de la désactivation d'une préférence");
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setDisabled(false);
                });
        }
    };

    const accountSection = accountUser ? (
        <AccountUi
            data={accountUser}
            handleClickOpen={handleClickOpen}
            open={open}
            handleClose={handleClose}
            handleSuccess={refreshAccountUser}
        />
    ) : (
        <Skeleton variant="rounded" height={420} className="accountPage__skeleton" />
    );

    const preferencesSection = accountUser ? (
        <MyPreferencesForm user={accountUser} handlerChangePrefs={handlerChangePrefs} disabled={disabled} />
    ) : (
        <Skeleton variant="rounded" height={260} className="accountPage__skeleton" />
    );

    //Conditionnement de la vue, plusieurs interfaces disponibles
    return (
        <div className="accountPage">
            <div className="accountPage__header">
                <Typography variant="h2">Mon compte</Typography>
                <Typography variant="body1">
                    Retrouvez vos informations personnelles et vos préférences de notification.
                </Typography>
            </div>

            <div className="accountPage__sections">
                <section className="accountPage__section">
                    <div className="accountPage__sectionCard">
                        <div className="accountPage__panel">{accountSection}</div>
                    </div>
                </section>

                <section className="accountPage__section">
                    <div className="accountPage__sectionCard">
                        <div className="accountPage__panel">{preferencesSection}</div>
                    </div>
                </section>
            </div>
        </div>
    );
}
